import React, { useCallback, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { Key } from '@mui/icons-material';
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import CustomModal from '../../../../components/ui/modal/modal.component';
import ClientPortalContactForm from '../../../../components/clientPortalContactForm/clientPortalContactForm.component';
import { PORTAL_CONTACTS_ENDPOINT, PORTAL_CONTACTS_REFRESH_EVENT } from '../../../../consts';
import { useHasPermission } from '../../../../contexts/auth';
import { formatDateTime } from '../../../../utils';
import { ClientPortalContact } from '../../../../types/portal';
import {
  containsStringFilterOperators,
  dateRangeFilterOperators,
} from '../../../../components/ui/customDataGrid/filterOperators';

export const ClientPortalAccess = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const canView = useHasPermission('portal.view_clientportalcontact');
  const canChange = useHasPermission('portal.change_clientportalcontact');

  const [editedContactId, setEditedContactId] = useState<number | null>(null);

  // Adding access lives in the toolbar; opening a row edits that contact.
  const openEdit = useCallback(
    (_rowId: string, row?: ClientPortalContact) => {
      if (!row || !canChange) return;

      setEditedContactId(row.id);
    },
    [canChange]
  );

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: t('Email address'),
      flex: 1,
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: 'full_name',
      headerName: t('Full name'),
      flex: 1,
      filterOperators: containsStringFilterOperators(),
      valueGetter: (_value, row) => row.full_name || '-',
    },
    {
      field: 'is_active',
      headerName: t('Status'),
      type: 'boolean',
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.is_active ? t('Active') : t('Revoked')}
          color={params.row.is_active ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'reported_jobs_count',
      headerName: t('Reported jobs'),
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      filterable: false,
      valueGetter: (_value, row) => row.reported_jobs_count ?? 0,
    },
    {
      field: 'last_login_at',
      headerName: t('Last login'),
      flex: 1,
      filterOperators: dateRangeFilterOperators(),
      renderCell: (params) => {
        if (!params.row.last_login_at) return '-';

        const [date, time] = formatDateTime(params.row.last_login_at).split('\n');
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 12, lineHeight: 1.15 }}>{date}</Typography>
            {time ? (
              <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.15 }}>
                {time}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
  ];

  if (!canView) {
    return null;
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <CustomDataGrid
        icon={<Key />}
        title={t('Portal access')}
        url={`${PORTAL_CONTACTS_ENDPOINT}?client=${id}`}
        gridSettingsKey="CLIENT_PORTAL_CONTACTS"
        columns={columns}
        onRowClick={openEdit}
        refreshEventName={PORTAL_CONTACTS_REFRESH_EVENT}
      />

      <CustomModal
        opened={editedContactId !== null}
        setOpened={(value) => {
          if (!value) setEditedContactId(null);
        }}
        title={t('Edit portal contact')}
        minWidth={480}
        content={
          <ClientPortalContactForm
            clientId={id ?? ''}
            contactId={editedContactId ?? undefined}
            onSuccess={() => {
              setEditedContactId(null);
              window.dispatchEvent(new Event(PORTAL_CONTACTS_REFRESH_EVENT));
            }}
          />
        }
      />
    </Box>
  );
};
